import { device } from '@accedo/xdk-core'
import { ID as WEBOS } from '@accedo/xdk-device-lg-webos'
import { ID as TIZEN } from '@accedo/xdk-device-samsung-tizen'
import { ID as VIZIO } from '@accedo/xdk-device-vizio-smartcast-contrib'
import { Lightning, Settings } from '@lightningjs/sdk'
import { AnalyticsBrowser } from '@segment/analytics-next'
import TvBaseCard from '../components/TvCards/TvBaseCard'
import TvGridRow from '../components/TvGridContainer/TvGridRow'
import { TvSearchGridRow } from '../components/TvSearchLayout/TvSearchGrid/TvSearchGridRow'
import { AppConfig } from '../services/appConfig'
import { AnalyticsModel } from '../types/flo-experience'
import { getUserData } from './authentication'
import { getAppName } from './info'

export enum NON_BFF_SEGMENT_SCREENS {
  Landing = 'Landing',
  Login = 'Login',
  AppExit = 'App Exit',
  ChangePassowrd = 'Change Password',
  ResetPassword = 'Reset Password',
  Settings = 'Settings',
  AccountDetails = 'Account Details',
  Subscription = 'Subscription',
  CustomerSupport = 'Customer Support',
  TermsAndConditions = 'Terms and Conditions',
  PrivacyPolicy = 'Privacy Policy',
  LiveScores = 'Live Scores',
  Paywall = 'Paywall',
  VideoPlayer = 'Video Player',
  Error = 'Error',
}

export enum SEGMENT_TRACK_EVENTS {
  ListItemClicked = 'List Item Clicked',
  ButtonClicked = 'Button Clicked',
  ApplicationOpened = 'Application Opened',
  SearchQueryMade = 'Search Query Made',
}

let cachedOptions = null

/**
 * To be used only on app's launch.
 * @returns AnalyticsBrowser instance (from Segment's library).
 */
export const initAnalytics = (): AnalyticsBrowser => {
  const writeKey: string = Settings.get('app', 'env.APP_SEGMENT_WRITE_KEY', '')

  try {
    return AnalyticsBrowser.load({ writeKey: writeKey })
  } catch {
    console.error('Analytics could not be initialized')
  }
}

/**
 * To be used at the app's launch, login, sign up, and sign out.
 */
export const captureSessionData = async () => {
  try {
    const { analyticsService } = AppConfig.shared
    const { user, user_id } = getUserData()
    const [userSubscription] = user?.user_subscriptions ?? []
    const favorites = await getFavorites()

    const properties: FloSegmentIdentify = {
      account_type: userSubscription?.type,
      created_at: userSubscription?.created_at,
      email: user?.email,
      facebook_id: user?.facebook_id,
      first_name: user?.first_name,
      last_name: user?.last_name,
      name: user?.first_name ? `${user.first_name} ${user.last_name}` : null,
      plan_type: userSubscription?.plan?.type,
      site_id: user?.site_id,
      subscription_end_date: userSubscription?.end_date,
      subscription_status: userSubscription?.status,
      username: user?.username,
      favorite_verticals: favorites.site?.join(','),
      favorite_teams: favorites.team?.join(','),
      favorite_athletes: favorites.person?.join(''),
      favorite_leagues: favorites.league?.join(','),
      favorite_content_categories: favorites.contentcategory?.join(','),
    }

    const options = await captureContext()

    analyticsService.identify(user_id?.toString(), properties, options)
  } catch (e) {
    console.error('There was a problem capturing session data for Segment. Details:', e)
  }
}

export const getFavorites = async () => {
  const { experienceApi } = AppConfig.shared
  const userCustomizations = await experienceApi.getUserCustomizations()

  const favorites: {
    site?: number[]
    team?: number[]
    league?: number[]
    person?: number[]
    contentcategory?: number[]
  } = {}
  userCustomizations.forEach(({ category }) => {
    const { externalId, type, orgType } = category

    if (!externalId) {
      return
    }

    if (type !== 'team' && !favorites[type]) {
      favorites[type] = []
    }

    if (type === 'team') {
      const favoriteType = orgType !== 'league' ? 'team' : 'league'

      if (!favorites[favoriteType]) {
        favorites[favoriteType] = []
      }

      favorites[favoriteType].push(externalId)
    } else {
      favorites[type].push(externalId)
    }
  })

  return favorites
}

const getPlatform = (platform) => {
  const platforms = {
    [WEBOS]: 'LG TV',
    [TIZEN]: 'Samsung TV',
    [VIZIO]: 'Vizio TV',
  }

  return platforms[platform] ?? 'workstation'
}

/**
 * Reports a screen view by calling the screen() function from Segment.
 * @param screenName The name of the screen.
 * @param properties Additional data to pass along to segment.
 * @param options Additional options to pass along to segment, such as additional context.
 */
export const screen = (screenName: string, properties: FloSegmentScreen, options = {}) => {
  const { analyticsService } = AppConfig.shared
  const platform = getPlatform(device.platform)

  analyticsService.screen(
    screenName,
    {
      platform,
      device: 'TV',
      ...properties,
    },
    options,
  )
}

/**
 * To be used when navigating to any page view.
 * @param {Object} analytics - An AnalyticsModel object, as provided by the BFF layout
 *
 * or
 *
 * @param {string} page_category - The page category
 * @param {string} subpage_category - The subpage category
 */
export const captureScreenData = async (...screenData) => {
  try {
    const [arg1, arg2] = screenData
    const isAnalyticsObject = typeof arg1 === 'object'
    const baseAnalytics = {
      extra_context: {
        page_category: null,
        subpage_category: null,
      },
    }

    if (!isAnalyticsObject) {
      baseAnalytics.extra_context.page_category = arg1
      baseAnalytics.extra_context.subpage_category = arg2
    }

    const analytics: AnalyticsModel = isAnalyticsObject ? arg1 : baseAnalytics
    const { page_category, subpage_category, site_ids, is_favorite } = analytics.extra_context
    const { site_name, site_id, entity_id, entity_type } = analytics

    let screenName = page_category
    if (subpage_category) {
      screenName += ` - ${subpage_category}`
    }

    const isEntity = screenName.includes('Entity')
    const isEvent = screenName.includes('Event')
    const isVideoCollection = screenName.includes('Video Collection')

    const properties: FloSegmentScreen = {
      page_category,
      subpage_category,
      site_ids,
      site_name,
      site_id,
      is_favorite,
      entity_id: isEntity || isEvent || isVideoCollection ? entity_id : null,
      entity_type: isEntity || isEvent || isVideoCollection ? entity_type : null,
    }

    const options = await captureContext()

    screen(screenName, properties, options)
  } catch (e) {
    console.error('There was a problem capturing the screen data for Segment. Details:', e)
  }
}

/**
 * Tracks an event by calling the track() function from Segment.
 * @param eventName The name of the triggered event.
 * @param properties Additional data to pass along to segment.
 * @param options Additional options to pass along to segment, such as additional context.
 */
export const track = (
  eventName: string,
  properties: FloSegmentScreen | FloSegmentTrack | FloSegmentSearchTrack | FloSegmentTrackAppOpened,
  options = {},
) => {
  const { analyticsService } = AppConfig.shared
  const platform = getPlatform(device.platform)

  analyticsService.track(
    eventName,
    {
      platform,
      device: 'TV',
      ...properties,
    },
    options,
  )
}

/**
 * To be used when clicking most buttons or cards.
 * @param {string} trackEvent - Either "Button Clicked" or "List Item Clicked"
 * @param {Object} analytics - An AnalyticsModel object, as provided by the BFF layout
 * @param {Object} [position] - An object containing the row index and carousel index
 * @param {number} [position.item_position] - The position of an item within a carousel or grid
 * @param {number} [position.row_number] - The row position of the selected item (only applicable to carousels)
 *
 * or
 *
 * @param {string} trackEvent - Either "Button Clicked" or "List Item Clicked"
 * @param {string} name - The name of the event to track
 * @param {string} page_category - The page category
 * @param {string} subpage_category - The subpage category
 */
export const captureClick = async (trackEvent: string, ...trackData) => {
  try {
    const [arg1, arg2, arg3] = trackData
    const isAnalyticsObject = typeof arg1 === 'object'
    const baseAnalytics = {
      name: null,
      extra_context: {
        page_category: null,
        subpage_category: null,
      },
    }

    if (arg1 === undefined || (isAnalyticsObject && arg1 === null)) {
      // Some cards do not contain analytics as they are simply needed to transition to another screen,
      // like when selecting sports from the sports picker screen to then add favorites
      return
    }

    if (!isAnalyticsObject) {
      baseAnalytics.name = arg1
      baseAnalytics.extra_context.page_category = arg2
      baseAnalytics.extra_context.subpage_category = arg3
    }

    const analytics: AnalyticsModel = isAnalyticsObject ? arg1 : baseAnalytics
    const position: FloSegmentCardPosition = isAnalyticsObject ? arg2 : {}

    const {
      page_category,
      subpage_category,
      site_ids,
      is_favorite,
      event_status,
      collection_name,
      live_id,
    } = analytics.extra_context
    const { site_name, site_id, entity_id, entity_type, name, primary_association_id } = analytics
    const { item_position, row_number } = position ?? {}

    const properties: FloSegmentTrack = {
      name,
      page_category,
      subpage_category,
      site_ids,
      site_name,
      site_id,
      is_favorite,
      entity_id,
      entity_type,
      event_status,
      collection_name,
      live_id,
      primary_association_id,
      row_number,
      item_position,
    }

    const options = await captureContext()

    track(trackEvent, properties, options)
  } catch (e) {
    console.error('There was a problem capturing the track data for Segment. Details:', e)
  }
}

/**
 * @param parent The parent Lightning Element of the card
 * @returns If the card or button is displayed within a grid or not
 */
export const isParentGrid = (parent: Lightning.Element) => {
  return parent instanceof TvGridRow || parent instanceof TvSearchGridRow
}

/**
 * @param parent The parent Lightning Element of the card
 * @returns The number of columns the grid is rendering
 */
export const gridColumnsCount = (parent: Lightning.Element) => {
  return isParentGrid(parent)
    ? (parent as TvGridRow).totalEl ?? (parent as TvSearchGridRow).totalCards
    : 3
}

/**
 * @param card The Lightning Element representing a card
 * @returns An object containing position data of the card needed by Segment, such as row and position numbers
 */
export const getCardPosition = (card: TvBaseCard) => {
  const isInGrid = isParentGrid(card?.parent)
  const columnsCount = gridColumnsCount(card?.parent)

  const rowNumber = isInGrid ? null : card?.rowIndex
  const itemPosition = isInGrid ? card?.rowIndex * columnsCount + card?.cardIndex : card?.cardIndex

  return {
    item_position: itemPosition,
    row_number: rowNumber,
  }
}

/**
 * To be used when searching.
 * @param {string} search_term - What the user searched for
 * @param {Object} analytics - An AnalyticsModel object, as provided by the BFF layout
 */
export const captureSearchQuery = async (search_term: string, analytics: AnalyticsModel) => {
  try {
    const { page_category, subpage_category, site_ids } = analytics.extra_context
    const { site_name, site_id } = analytics
    const trackEvent = SEGMENT_TRACK_EVENTS.SearchQueryMade

    const properties: FloSegmentSearchTrack = {
      search_term,
      page_category,
      subpage_category,
      site_ids,
      site_name,
      site_id,
    }

    const options = await captureContext()

    track(trackEvent, properties, options)
  } catch (e) {
    console.error('There was a problem capturing the track data for Segment. Details:', e)
  }
}

/**
 * To be called once the application starts running.
 */
export const captureAppOpened = async () => {
  try {
    const trackEvent = SEGMENT_TRACK_EVENTS.ApplicationOpened
    const options = await captureContext()

    track(trackEvent, { name: SEGMENT_TRACK_EVENTS.ApplicationOpened }, options)
  } catch (e) {
    console.error('There was a problem capturing the track data for Segment. Details:', e)
  }
}

/**
 * Gathers the common fields needed by Segment that the web implementation by default is unable to retrieve
 * @returns An object containing the missing context properties that teh analytics.js library implementation is unable to retrieve
 */

export const captureContext = async () => {
  if (cachedOptions) {
    return cachedOptions
  }

  let screen = { width: window.innerWidth, height: window.innerHeight }
  try {
    screen = device.system.getDeviceResolution()
  } catch (e) {
    console.error('There was a problem capturing the screen context data for Segment. Details:', e)
  }

  const [language, country] = navigator.language.split('-')
  let demographics = {
    country,
    language,
  }
  try {
    demographics = {
      country: device.system.getCountry(),
      language,
    }
  } catch (e) {
    console.error(
      'There was a problem capturing the demographics context data for Segment. Details:',
      e,
    )
  }

  const options: FloSegmentOptions = {
    context: {
      app: {
        name: getAppName(),
        version: Settings.get('app', 'env.APP_VERSION', null),
      },
      demographics: {
        ...demographics,
      },
      model: {
        ...(await AppConfig.shared.appSettings.device.getModel()),
      },
      os: {
        ...AppConfig.shared.appSettings.device.getOS(),
      },
      platform: {
        name: getPlatform(device.platform),
      },
      screen: {
        ...screen,
      },
    },
  }

  cachedOptions = options

  return options
}

/**
 * Retrieves the anonymous ID created by Segment
 * @returns the anonymous ID created by Segment
 */
export const getAnonymousId = async () => {
  const { analyticsService } = AppConfig.shared
  const user = await analyticsService.user()
  return user.anonymousId()
}
